import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ProcessRoadmap = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [showPopover, setShowPopover] = useState(null);
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: false
  });

  const processSteps = [
    {
      id: 1,
      title: "Consultation & Requirements Gathering",
      subtitle: "Understanding your vision and goals.",
      description: "We begin by learning what you want from your website.",
      icon: "🔍",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20",
      borderColor: "border-blue-200 dark:border-blue-700",
      position: { x: 15, y: 15 },
      mobilePosition: { x: 8, y: 15 },
      details: ["Talk about your goals and ideas", "Learn who your customers are", "Note what pages and features you need", "Get your design preferences"]
    },
    {
      id: 2,
      title: "Timeline & Price Discussion",
      subtitle: "Agreeing on plan and budget",
      description: "We clearly explain what’s included, how long it will take, and the total cost.",
      icon: "📋",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20",
      borderColor: "border-purple-200 dark:border-purple-700",
      position: { x: 25, y: 8 },
      mobilePosition: { x: 22, y: 8 },
      details: ["Share how long the work will take", "Break the project into clear steps", "Set the budget","Finalize what’s included"]
    },
    {
      id: 3,
      title: "Rough Design",
      subtitle: "Showing you the basic look",
      description: "We create a simple layout to give you a feel for the look and flow.",
      icon: "🎨",
      color: "from-emerald-500 to-teal-500",
      bgColor: "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20",
      borderColor: "border-emerald-200 dark:border-emerald-700",
      position: { x: 35, y: 22 },
      mobilePosition: { x: 37, y: 22 },
      details: ["Make a basic draft of the homepage", " Choose colors, fonts, and layout", "Match your branding", "Share the design for your thoughts"]
    },
    {
      id: 4,
      title: "First Draft – Open to Feedback",
      subtitle: "First full version for your review",
      description: "We build a working version of the site and invite your feedback.",
      icon: "⚡",
      color: "from-orange-500 to-red-500",
      bgColor: "bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20",
      borderColor: "border-orange-200 dark:border-orange-700",
      position: { x: 45, y: 12 },
      mobilePosition: { x: 52, y: 12 },
      details: ["Show how the website works", "Let you click and explore", "Listen to your feedback", "Note what needs to be changed"]
    },
    {
      id: 5,
      title: "Final Draft with Updated Changes",
      subtitle: "Making it just right",
      description: "We make all needed updates to get the website ready for launch.",
      icon: "🔧",
      color: "from-indigo-500 to-blue-500",
      bgColor: "bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20",
      borderColor: "border-indigo-200 dark:border-indigo-700",
      position: { x: 55, y: 20 },
      mobilePosition: { x: 60, y: 25 }, 
      details: ["Apply your changes", "Final check for errors", "Update all content", "Get your final approval"]
    },
    {
      id: 6,
      title: "Handover",
      subtitle: "Giving you full access",
      description: "We deliver the finished website and guide you on how to use it.",
      icon: "🚀",
      color: "from-yellow-500 to-orange-500",
      bgColor: "bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20",
      borderColor: "border-yellow-200 dark:border-yellow-700",
      position: { x: 65, y: 14 },
      mobilePosition: { x: 75, y: 8 }, 
      details: ["Share login and access details", "Help with launch (if needed)", "Show you how to manage it"]
    },
    {
      id: 7,
      title: "30-Day Free Support",
      subtitle: "We're here if you need us",
      description: "You get a month of free help for any small fixes or questions.",
      icon: "📈",
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20",
      borderColor: "border-green-200 dark:border-green-700",
      position: { x: 75, y: 18 },
      mobilePosition: { x: 85, y: 35 }, 
      details: ["Fix small bugs or issues", "Answer any questions", " Help with small edits", "Make sure everything runs smoothly"]
    }
  ];

  // Auto-cycle through steps
  useEffect(() => {
    if (!inView) return;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % processSteps.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [inView, processSteps.length]);

  // Add isDesktop and isTablet for breakpoint logic
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkScreen = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1200);
      setIsDesktop(width >= 1200);
      
      // Debug logging to verify breakpoints
      console.log(`Screen: ${width}px - Mobile: ${width < 768}, Tablet: ${width >= 768 && width < 1200}, Desktop: ${width >= 1200}`);
    };
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  // Fix handleNodeClick to handle ALL non-mobile screen sizes
  const handleNodeClick = (idx) => {
    if (isMobile) {
      // Mobile: just set active step to show content in center area
      setActiveStep(idx);
    } else {
      // All non-mobile sizes (tablet, intermediate, desktop): toggle popover on click
      if (showPopover === idx) {
        // If clicking the same node, close it
        setShowPopover(null);
      } else {
        // If clicking a different node, close current and open new one with slight delay
        setShowPopover(null);
        setTimeout(() => {
          setShowPopover(idx);
        }, 50); // Small delay to prevent flickering
      }
    }
  };

  // Generate pipeline path
  const generatePipelinePath = (isMobileOrTablet = false) => {
    const positions = isMobileOrTablet ? 'mobilePosition' : 'position';
    // Clamp mobile x for last node to 85 (prevents overflow)
    const steps = processSteps.map((step, idx) => {
      if (isMobileOrTablet && idx === processSteps.length - 1 && step[positions].x > 85) {
        return { ...step, [positions]: { ...step[positions], x: 85 } };
      }
      return step;
    });
    
    // Get actual container width for responsive calculations
    const containerWidth = isMobileOrTablet ? 800 : 1000;
    const containerHeight = isMobileOrTablet ? 200 : 200;
    const offsetY = isMobileOrTablet ? 100 : 100;
    
    const points = steps.map(step => ({
      x: (step[positions].x / 100) * containerWidth,
      y: (step[positions].y / 100) * containerHeight + offsetY
    }));
    
    // Make path extension responsive to actual screen width
    if (!isMobileOrTablet) {
      // Calculate extension based on screen width ratio - increase the multiplier for better connection
      const screenWidth = window.innerWidth;
      const extensionFactor = Math.max(50, Math.min(120, screenWidth * 0.08)); // Increased from 0.05 to 0.08
      
      points[0].x = Math.max(0, points[0].x - extensionFactor);
      points[points.length-1].x = Math.min(containerWidth, points[points.length-1].x + extensionFactor);
    }
    
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx1 = prev.x + (curr.x - prev.x) * 0.5;
      const cpy1 = prev.y;
      const cpx2 = prev.x + (curr.x - prev.x) * 0.5;
      const cpy2 = curr.y;
      path += ` C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${curr.x} ${curr.y}`;
    }
    return path;
  };

  // Detect mobile screen
  const getPopoverPosition = (stepPosition, isMobileOrTablet) => {
    if (isMobileOrTablet) {
      return {
        top: '',
        left: '',
        width: 'w-11/12 max-w-sm',
        mobile: true
      };
    }
    if (stepPosition.x > 70) {
      return {
        top: 'top-20',
        left: 'right-0',
        width: 'w-80',
        mobile: false
      };
    } else if (stepPosition.x < 30) {
      return {
        top: 'top-20',
        left: 'left-0',
        width: 'w-80',
        mobile: false
      };
    } else {
      return {
        top: 'top-20',
        left: 'left-1/2 transform -translate-x-1/2',
        width: 'w-80',
        mobile: false
      };
    }
  };

  // Mobile/tablet outside click detection
  useEffect(() => {
    if (!(isMobile || isTablet)) return;
    const handleClick = (e) => {
      // Add more specific targeting to prevent conflicts
      if (!e.target.closest('.process-popover') && 
          !e.target.closest('.process-node') && 
          !e.target.closest('button')) {
        setShowPopover(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMobile, isTablet]);

  return (
<section
  id="process"
  className={`${isMobile ? 'py-[6rem]' : 'py-24'} px-4 relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 dark:from-slate-900 dark:via-blue-900/10 dark:to-purple-900/10`}
>      {/* Advanced Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" className="absolute inset-0">
            <defs>
              <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="1" className="text-blue-200 dark:text-blue-800"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Floating Orbs */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-r from-blue-400/20 to-purple-400/20 blur-xl"
            style={{
              width: `${60 + Math.random() * 120}px`,
              height: `${60 + Math.random() * 120}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              x: [0, Math.random() * 100 - 50, 0],
              y: [0, Math.random() * 100 - 50, 0],
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 4,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <div ref={ref} className="max-w-7xl mx-auto relative z-10">
        {/* Enhanced Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="inline-block mb-4 px-6 py-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full border border-blue-200 dark:border-blue-700"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <span className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Our Proven Methodology
            </span>
          </motion.div>
          
          <motion.h2 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight"
            animate={{ 
              backgroundPosition: ["0%", "100%", "0%"],
            }}
            transition={{ duration: 10, repeat: Infinity }}
          >
            Process We Follow
          </motion.h2>
          
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 dark:text-slate-300 max-w-4xl mx-auto leading-relaxed">
          Experience our 7-stage process that turns complex ideas into exceptional digital solutions.
          </p>
        </motion.div>

        {/* Advanced Pipeline Visualization */}
        <div className={`relative ${isMobile ? 'h-[8rem]' : isTablet ? 'h-80' : 'h-96'} ${isMobile ? 'mb-4' : 'mb-16'} mobile-nodes px-4`} >
          {/* Main Pipeline SVG */}
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none z-10" 
            viewBox={isMobile || isTablet ? "0 0 800 300" : "0 0 1000 400"}
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Pipeline Base Path */}
            <motion.path
              d={generatePipelinePath(isMobile)}
              stroke="url(#pipelineGradient)"
              strokeWidth={isMobile || isTablet ? "6" : "8"}
              fill="none"
              strokeLinecap="round"
              strokeDasharray="0"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={inView ? { pathLength: 1, opacity: 0.8 } : { pathLength: 0, opacity: 0 }}
              transition={{ duration: 3, ease: "easeInOut" }}
            />

            {/* Pipeline Glow Effect */}
            <motion.path
              d={generatePipelinePath(isMobile)}
              stroke="url(#pipelineGradient)"
              strokeWidth={isMobile || isTablet ? "12" : "16"}
              fill="none"
              strokeLinecap="round"
              opacity="0.3"
              filter="blur(4px)"
              initial={{ pathLength: 0 }}
              animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
              transition={{ duration: 3, ease: "easeInOut", delay: 0.5 }}
            />

            {/* Animated Flow Particles */}
            {[...Array(isMobile ? 3 : 5)].map((_, i) => (
              <motion.circle
                key={i}
                r={isMobile ? "4" : "6"}
                fill="url(#flowParticle)"
                initial={{ opacity: 0 }}
                animate={inView ? { 
                  opacity: [0, 1, 0],
                } : { opacity: 0 }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  delay: i * 0.8,
                  ease: "linear"
                }}
              >
                <animateMotion
                  dur="8s"
                  repeatCount="indefinite"
                  path={generatePipelinePath(isMobile)}
                  begin={`${i * 1.6}s`}
                />
              </motion.circle>
            ))}

            {/* Gradient Definitions */}
            <defs>
              <linearGradient id="pipelineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="1" />
                <stop offset="25%" stopColor="rgb(147, 51, 234)" stopOpacity="1" />
                <stop offset="50%" stopColor="rgb(236, 72, 153)" stopOpacity="1" />
                <stop offset="75%" stopColor="rgb(251, 146, 60)" stopOpacity="1" />
                <stop offset="100%" stopColor="rgb(34, 197, 94)" stopOpacity="1" />
              </linearGradient>
              <linearGradient id="pipelineGlow" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.4" />
                <stop offset="50%" stopColor="rgb(147, 51, 234)" stopOpacity="0.4" />
                <stop offset="100%" stopColor="rgb(34, 197, 94)" stopOpacity="0.4" />
              </linearGradient>
              <radialGradient id="flowParticle">
                <stop offset="0%" stopColor="rgb(255, 255, 255)" stopOpacity="1" />
                <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0.8" />
              </radialGradient>
            </defs>
          </svg>

          {/* Interactive Process Nodes */}
          {processSteps.map((step, index) => {
            const isActive = activeStep === index;
            const isHovered = hoveredStep === index;
            const isCompleted = activeStep > index;
            const positions = (isMobile) ? 'mobilePosition' : 'position';
            
            return (
              <motion.div
                key={step.id}
                className={`absolute cursor-pointer group process-node`}
                style={{
                  left: `${step[positions].x}%`,
                  top: `${step[positions].y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={inView ? { 
                  opacity: 1, 
                  scale: 1,
                  transition: { delay: index * 0.2, duration: 0.6 }
                } : { opacity: 0, scale: 0 }}
                onMouseOver={isDesktop ? (() => { 
                  setHoveredStep(index); 
                  setShowPopover(index); 
                }) : undefined}
                onMouseOut={isDesktop ? (() => { 
                  setHoveredStep(null); 
                  setShowPopover(null); 
                }) : undefined}
                onClick={isMobile ? (() => setActiveStep(index)) : (() => handleNodeClick(index))}
              >
                {/* Node Circle */}
                <motion.div
                  className={`relative ${isMobile ? 'w-12 h-12' : 'w-16 h-16'} rounded-full ${step.bgColor} ${step.borderColor} border-2 shadow-xl backdrop-blur-sm flex items-center justify-center z-20`}
                  animate={{
                    scale: isActive ? (isMobile ? 1.2 : 1.3) : isHovered ? (isMobile ? 1.1 : 1.15) : 1,
                    boxShadow: isActive 
                      ? "0 20px 40px rgba(59, 130, 246, 0.4), 0 0 0 8px rgba(59, 130, 246, 0.1)" 
                      : isHovered 
                      ? "0 15px 30px rgba(0, 0, 0, 0.2), 0 0 0 4px rgba(59, 130, 246, 0.05)" 
                      : "0 8px 16px rgba(0, 0, 0, 0.1)"
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  {/* Icon */}
                  <motion.div
                    className={isMobile ? "text-lg" : "text-2xl"}
                    animate={{
                      rotate: isActive ? [0, 360] : 0,
                      scale: isActive ? [1, 1.2, 1] : 1
                    }}
                    transition={{
                      rotate: { duration: 2, repeat: isActive ? Infinity : 0, ease: "linear" },
                      scale: { duration: 1, repeat: isActive ? Infinity : 0, repeatType: "reverse" }
                    }}
                  >
                    {step.icon}
                  </motion.div>

                  {/* Completion Indicator */}
                  {isCompleted && (
                    <motion.div
                      className={`absolute -top-1 -right-1 ${isMobile ? 'w-4 h-4' : 'w-6 h-6'} bg-green-500 rounded-full flex items-center justify-center shadow-lg`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, delay: 0.3 }}
                    >
                      <svg className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} text-white`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </motion.div>
                  )}

                  {/* Step Number */}
                  <motion.div
                    className={`absolute ${isMobile ? '-bottom-6' : '-bottom-8'} left-1/2 transform -translate-x-1/2 ${isMobile ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'} rounded-full bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 flex items-center justify-center font-bold text-slate-700 dark:text-slate-200 shadow-lg`}
                    animate={{
                      y: isActive ? [0, -4, 0] : 0,
                      scale: isActive ? 1.1 : 1
                    }}
                    transition={{ duration: 2, repeat: isActive ? Infinity : 0 }}
                  >
                    {step.id}
                  </motion.div>

                  {/* Pulsing Ring for Active Step */}
                  {isActive && (
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-blue-400"
                      animate={{
                        scale: [1, 2, 1],
                        opacity: [0.8, 0, 0.8]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  )}
                </motion.div>

                {/* Enhanced Popover */}
                <AnimatePresence>
                  {!isMobile && showPopover === index && (
                    <motion.div
                      className={`absolute z-30 ${getPopoverPosition(step.position, false).top} ${getPopoverPosition(step.position, false).left} ${getPopoverPosition(step.position, false).width} process-popover`}
                      initial={{ opacity: 0, y: -10, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.9 }}
                      transition={{ duration: 0.18, ease: 'easeInOut' }}
                    >
                      <div className={`relative p-6 rounded-2xl ${step.bgColor} ${step.borderColor} border-2 shadow-2xl backdrop-blur-lg`}>
                        {/* Popover Arrow */}
                        <div className={`absolute -top-2 ${step.position.x > 70 ? 'right-6' : step.position.x < 30 ? 'left-6' : 'left-1/2 transform -translate-x-1/2'} w-4 h-4 ${step.bgColor} ${step.borderColor} border-t-2 border-l-2 rotate-45`} />
                        
                        {/* Close Button for non-desktop */}
                        {!isDesktop && (
                          <button
                            className="absolute top-2 right-2 text-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPopover(null);
                            }}
                            aria-label="Close"
                          >
                            ×
                          </button>
                        )}
                        
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xl sm:text-2xl md:text-3xl">{step.icon}</span>
                          <div>
                            <div className="font-semibold text-sm sm:text-base md:text-lg text-slate-700 dark:text-slate-100">{step.title}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-300">{step.subtitle}</div>
                          </div>
                        </div>
                        <div className="text-xs sm:text-sm md:text-base text-slate-600 dark:text-slate-200 mb-2">{step.description}</div>
                        <ul className="list-disc pl-5 text-xs md:text-sm text-slate-500 dark:text-slate-300">
                          {step.details.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile Content Display Area */}
        {isMobile && (
          <div className="px-4 mb-8">
            <motion.div
              className="w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className={`p-4 sm:p-6 rounded-2xl ${processSteps[activeStep].bgColor} ${processSteps[activeStep].borderColor} border-2 shadow-xl backdrop-blur-lg`}>
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <span className="text-2xl sm:text-3xl">{processSteps[activeStep].icon}</span>
                  <div>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-slate-700 dark:text-slate-100">
                      {processSteps[activeStep].title}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-300">
                      {processSteps[activeStep].subtitle}
                    </p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm md:text-base text-slate-600 dark:text-slate-200 mb-3 sm:mb-4 leading-relaxed">
                  {processSteps[activeStep].description}
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {processSteps[activeStep].details.map((detail, idx) => (
                    <motion.div
                      key={idx}
                      className="flex items-center text-xs sm:text-sm text-slate-500 dark:text-slate-400"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${processSteps[activeStep].color} mr-3`} />
                      {detail}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Enhanced Progress Control */}
        <motion.div
          className="flex flex-col items-center space-y-6"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ delay: 2, duration: 0.8 }}
        >
          {/* Progress Bar */}
          <div className="w-full max-w-2xl bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 rounded-full"
              animate={{
                width: `${((activeStep + 1) / processSteps.length) * 100}%`
              }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            />
          </div>

          {/* Step Indicators */}
          <div className="flex space-x-4">
            {processSteps.map((_, index) => (
              <motion.button
                key={index}
                className={`w-4 h-4 rounded-full transition-all duration-300 ${
                  activeStep === index 
                    ? 'bg-blue-500 shadow-lg' 
                    : activeStep > index
                    ? 'bg-green-500'
                    : 'bg-slate-300 dark:bg-slate-600 hover:bg-blue-300'
                }`}
                animate={{
                  scale: activeStep === index ? 1.4 : 1,
                  boxShadow: activeStep === index 
                    ? "0 20px 40px rgba(59, 130, 246, 0.4), 0 0 0 8px rgba(59, 130, 246, 0.1)" 
                    : "none"
                }}
                onClick={() => setActiveStep(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>

          {/* Current Step Info */}
          <motion.div
            className="text-center max-w-md"
            key={activeStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className={`text-base sm:text-lg md:text-xl font-bold mb-2 bg-gradient-to-r ${processSteps[activeStep].color} bg-clip-text text-transparent`}>
              {processSteps[activeStep].title}
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
              {processSteps[activeStep].subtitle}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessRoadmap;
